import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import './FlowCreator.css';

const FlowCreator = () => {
  usePageTitle('New Flow');
  const navigate = useNavigate();
  const [creationType, setCreationType] = useState(null);

  const creationOptions = [
    {
      id: 'simple',
      icon: '⚡',
      title: 'Odoo Extract',
      description: 'Extract data from Odoo models',
      features: [
        'Single Odoo model',
        'Visual field selection',
        'Auto-configuration',
        'Ready in 2 minutes'
      ],
      color: '#3b82f6',
      recommended: true
    },
    {
      id: 'sql_transform',
      icon: '🔄',
      title: 'SQL Transform',
      description: 'Transform data with SQL queries',
      features: [
        'Write SQL in BigQuery',
        'MERGE/Upsert support',
        'Auto lineage tracking',
        'Build Silver layer tables'
      ],
      color: '#f59e0b'
    },
    {
      id: 'multi_source',
      icon: '🔌',
      title: 'Multi-Source Pipeline',
      description: 'Combine data from multiple sources',
      features: [
        'Multiple data sources (Odoo, CSV, API)',
        'JOIN/UNION operations',
        'Data quality checks',
        'Advanced transformations'
      ],
      color: '#8b5cf6'
    },
    {
      id: 'template',
      icon: '📋',
      title: 'Use Template',
      description: 'Start from pre-built template',
      features: [
        'Partners template',
        'Products template',
        'Sales Orders template',
        'Customize as needed'
      ],
      color: '#10b981'
    }
  ];

  const templates = [
    {
      id: 'partners',
      icon: '👥',
      name: 'Partners ETL',
      description: 'Extract partners from Odoo with validation',
      model: 'res.partner'
    },
    {
      id: 'products',
      icon: '📦',
      name: 'Products ETL',
      description: 'Extract products with pricing and categories',
      model: 'product.product'
    },
    {
      id: 'sales',
      icon: '🛒',
      name: 'Sales Orders ETL',
      description: 'Extract sales orders with customer and product details',
      model: 'sale.order.line'
    },
    {
      id: 'purchases',
      icon: '🛍️',
      name: 'Purchase Orders ETL',
      description: 'Extract purchase orders with vendor details',
      model: 'purchase.order.line'
    }
  ];

  const handleSelection = (type) => {
    if (type === 'simple') {
      navigate('/flows/new/field-selector');
    } else if (type === 'sql_transform') {
      navigate('/flows/new/sql-transform');
    } else if (type === 'multi_source') {
      navigate('/flows/new/multi-source');
    } else if (type === 'template') {
      setCreationType('template');
    }
  };

  const selectTemplate = async (template) => {
    // TODO: Create flow from template
    alert(`Creating ${template.name}... (Implementation pending)`);
  };

  if (creationType === 'template') {
    return (
      <div className="flow-creator-container">
        <div className="creator-header">
          <button onClick={() => setCreationType(null)} className="btn-back">
            ← Back to Options
          </button>
          <h1>Choose a Template</h1>
        </div>

        <div className="templates-grid">
          {templates.map((template) => (
            <button
              key={template.id}
              className="template-card"
              onClick={() => selectTemplate(template)}
            >
              <div className="template-icon">{template.icon}</div>
              <h3>{template.name}</h3>
              <p>{template.description}</p>
              <code className="template-model">{template.model}</code>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flow-creator-container">
      <div className="creator-header">
        <button onClick={() => navigate('/flows')} className="btn-back">
          ← Back to Flows
        </button>
        <div>
          <h1>Create New ETL Flow</h1>
          <p className="header-description">Choose how you want to create your pipeline</p>
        </div>
      </div>

      <div className="options-grid">
        {creationOptions.map((option) => (
          <div
            key={option.id}
            className="option-card"
            style={{ borderColor: option.color }}
            onClick={() => handleSelection(option.id)}
          >
            {option.recommended && (
              <div className="recommended-badge">Recommended</div>
            )}
            <div className="option-icon" style={{ color: option.color }}>
              {option.icon}
            </div>
            <h2>{option.title}</h2>
            <p className="option-description">{option.description}</p>
            
            <ul className="feature-list">
              {option.features.map((feature, idx) => (
                <li key={idx}>
                  <span className="check-icon">✓</span>
                  {feature}
                </li>
              ))}
            </ul>

            <button 
              className="option-btn"
              style={{ background: option.color }}
            >
              Get Started →
            </button>
          </div>
        ))}
      </div>

      <div className="info-section">
        <h3>🤔 Which option should I choose?</h3>
        <div className="info-grid">
          <div className="info-card">
            <strong>Choose Simple Flow if:</strong>
            <ul>
              <li>You need data from ONE Odoo model</li>
              <li>You want the fastest setup</li>
              <li>You're new to the platform</li>
            </ul>
          </div>
          <div className="info-card">
            <strong>Choose SQL Transform if:</strong>
            <ul>
              <li>You want to build Silver/Gold layers</li>
              <li>You need to JOIN, aggregate, or clean data</li>
              <li>You want automatic data lineage</li>
            </ul>
          </div>
          <div className="info-card">
            <strong>Choose Multi-Source if:</strong>
            <ul>
              <li>You need to combine multiple sources</li>
              <li>You have CSV files or API data</li>
              <li>You need advanced transformations</li>
            </ul>
          </div>
          <div className="info-card">
            <strong>Choose Template if:</strong>
            <ul>
              <li>You want a pre-configured pipeline</li>
              <li>You're extracting common objects</li>
              <li>You want best practices built-in</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlowCreator;

